import { deriveSlideTextSubject } from "../outline/outlineSlide.js";
import { isComposedBackgroundMode } from "../style/presets.js";
import { compositionRegionPromptLines, getCompositionSpec, } from "../style/composition.js";
import { stylePalettePromptSegments, styleTreatmentForPrompt, } from "../style/stylePrompt.js";
import { composeContextualImagePrompt } from "./slideImageService.js";
function compositionHintsForRole(role) {
    switch (role) {
        case "cover":
            return "Cover slide: bold hero illustration on one side, large calm text zone on the other, strong visual hierarchy.";
        case "section":
            return "Section divider: atmospheric scene with clear negative space band for section title, fewer visual elements.";
        case "content":
        default:
            return "Content slide: subject illustration integrated with layout grid, reserved calm band for title and bullets, balanced composition.";
    }
}
/** F23/U6 — composed background: subject-first illustration, style as treatment. */
export function buildComposedBackgroundPrompt(style, slide, slideIndex) {
    const treatment = styleTreatmentForPrompt(style);
    const subject = slide.visualBrief?.subject?.trim() ||
        slide.outlineVisual?.imageSubject?.trim() ||
        deriveSlideTextSubject(slide);
    const spec = getCompositionSpec(slide.composition);
    const role = slide.outlineRole ?? "content";
    return [
        "16:9 presentation slide artwork: one coherent illustration that depicts the subject below, rendered in the given visual style.",
        `Main subject (focus of the image, authoritative): ${subject}`,
        compositionHintsForRole(role),
        ...compositionRegionPromptLines(spec),
        `Visual style / treatment: ${treatment}`,
        ...stylePalettePromptSegments(style),
        `Realism: ${style.backgroundRealism}`,
        `Slide ${slideIndex + 1} (${role}).`,
        "CRITICAL: NO readable text, numbers, labels, logos, charts, or typography in the image — text is overlaid later as editable widgets.",
    ].join(" ");
}
export function resolveSlideVisualPrompt(style, slide, slideIndex, options) {
    const mode = options?.mode ?? "background";
    const allowSaved = options?.allowSavedPrompt ?? mode === "background";
    if (allowSaved) {
        const saved = slide.background.prompt?.trim();
        if (saved)
            return saved;
    }
    if (mode === "background" && isComposedBackgroundMode(style)) {
        return buildComposedBackgroundPrompt(style, slide, slideIndex);
    }
    const subject = options?.subject ?? deriveSlideTextSubject(slide);
    return composeContextualImagePrompt(style, subject, mode, slideIndex);
}
export function resolveSlideImagePrompt(style, slide, slideIndex) {
    return resolveSlideVisualPrompt(style, slide, slideIndex);
}
export function shouldAutoGenerateVisualWidgets(theme) {
    return !isComposedBackgroundMode(theme);
}
export function deckUsesComposedBackground(theme) {
    return isComposedBackgroundMode(theme);
}
export function composedDeckHint(deck) {
    return {
        mode: deck.theme.composition ?? "composed-background",
        slideCount: deck.slides.length,
    };
}
